import { useContext, useState } from 'react'
import { CheckOutlined, WarningAmberOutlined } from '@mui/icons-material'
import { Grid, Pagination, Skeleton, Typography } from '@mui/material'

import AlertCard from '../../components/alert-card'
import ButtonGroup from '../../components/m3/button-group'
import DashboardScreen from '../../components/screen/dashboard-screen'
import SelectedFolderBanner from '../../components/selected-folder-banner'
import StickyHeader from '../../components/sticky-header'
import UrgentAlertsBanner from '../../components/urgent-alerts-banner'
import successManSvg from '../../images/success-man.svg'
import { DashboardContext } from '../../providers/dashboard-provider'
import useDates from '../../utils/hooks/useDates'
import useIsMobile from '../../utils/hooks/useIsMobile'

import { groupAlertsByDate } from './helpers'
import useStyles from './index.styles'

const Alerts = () => {
  const classes = useStyles
  const { folder, activeAlerts, resolvedAlerts, loading, error } = useContext(DashboardContext)
  const isMobile = useIsMobile()
  const { formatCalendarDate, formatDateLongMonth } = useDates()

  const [selectedCategory, setSelectedCategory] = useState<'active' | 'resolved'>('active')
  const [activePage, setActivePage] = useState(1)
  const [resolvedPage, setResolvedPage] = useState(1)

  const getAlertDateGroupLabel = (date: Date) => {
    const nearDateLabel = formatCalendarDate(date)
    if (nearDateLabel.startsWith('Today')) return 'Today'
    if (nearDateLabel.startsWith('Yesterday')) return 'Yesterday'
    return formatDateLongMonth(date)
  }

  const PAGE_SIZE = 5
  const groupedActiveAlerts = groupAlertsByDate(activeAlerts, activePage, PAGE_SIZE, getAlertDateGroupLabel)
  const groupedResolvedAlerts = groupAlertsByDate(resolvedAlerts, resolvedPage, PAGE_SIZE, getAlertDateGroupLabel)
  const isActiveCategory = selectedCategory === 'active'
  const currentAlerts = isActiveCategory ? activeAlerts : resolvedAlerts
  const currentGroupedAlerts = isActiveCategory ? groupedActiveAlerts : groupedResolvedAlerts
  const currentPage = isActiveCategory ? activePage : resolvedPage
  const setCurrentPage = isActiveCategory ? setActivePage : setResolvedPage
  const activeUrgentAlerts = activeAlerts.filter((alert) => alert.tags.includes('urgent') && alert.responseHistory.length === 0)

  return (
    <DashboardScreen error={error} headerProps={{ showLogo: true }}>
      {isMobile && <SelectedFolderBanner folder={folder} />}

      {!isMobile && (
        <StickyHeader marginBottom={1}>
          <Grid container={true} size={12} sx={classes.alertsHeaderRow}>
            <Typography variant="body2" sx={classes.alertsHeader}>
              <WarningAmberOutlined /> Alerts {activeAlerts.length}
            </Typography>
          </Grid>
          {!loading && (
            <ButtonGroup fullWidth={true} sx={classes.categoryButtonGroup}>
              <ButtonGroup.Button
                selected={selectedCategory === 'active'}
                icon={<CheckOutlined fontSize="small" />}
                onClick={() => {
                  setSelectedCategory('active')
                  setActivePage(1)
                }}
              >
                Active Alerts ({activeAlerts.length})
              </ButtonGroup.Button>
              <ButtonGroup.Button
                selected={selectedCategory === 'resolved'}
                icon={undefined}
                onClick={() => {
                  setSelectedCategory('resolved')
                  setResolvedPage(1)
                }}
              >
                Resolved Alerts ({resolvedAlerts.length})
              </ButtonGroup.Button>
            </ButtonGroup>
          )}
        </StickyHeader>
      )}

      {loading ? (
        <Grid container={true} sx={classes.skeletonGrid}>
          <Skeleton variant="rounded" width="100%" height={37} sx={classes.skeletonButtonGroup} />
          <Skeleton width={60} sx={classes.skeletonDateHeader} />
          <Skeleton variant="rounded" width="100%" height={74} />
          <Skeleton variant="rounded" width="100%" height={74} />
        </Grid>
      ) : (
        <>
          {isMobile && (
            <ButtonGroup fullWidth={true} sx={classes.categoryButtonGroup}>
              <ButtonGroup.Button
                selected={selectedCategory === 'active'}
                icon={<CheckOutlined fontSize="small" />}
                onClick={() => {
                  setSelectedCategory('active')
                  setActivePage(1)
                }}
              >
                Active Alerts ({activeAlerts.length})
              </ButtonGroup.Button>
              <ButtonGroup.Button
                selected={selectedCategory === 'resolved'}
                icon={undefined}
                onClick={() => {
                  setSelectedCategory('resolved')
                  setResolvedPage(1)
                }}
              >
                Resolved Alerts ({resolvedAlerts.length})
              </ButtonGroup.Button>
            </ButtonGroup>
          )}

          {currentAlerts.length === 0 && (
            <Grid container={true} sx={classes.noAlertsContainer}>
              <img src={successManSvg} alt="Success" style={{ width: '200px', height: 'auto' }} />

              <Typography variant="body1" sx={classes.noAlertsText}>
                Looking Great! You are connected and protected
              </Typography>
            </Grid>
          )}

          {currentAlerts.length > 0 && (
            <>
              {selectedCategory === 'active' && activeUrgentAlerts.length > 0 && <UrgentAlertsBanner count={activeUrgentAlerts.length} />}
              <Grid container={true} sx={classes.alertsListContainer}>
                {currentGroupedAlerts.map((group) => {
                  return (
                    <Grid container={true} key={group.key} sx={classes.alertGroupContainer}>
                      <Grid container={true} sx={classes.alertGroupHeader}>
                        <Typography variant="overline">{group.label}</Typography>
                      </Grid>

                      {group.alerts.map(({ alert }) => {
                        return <AlertCard alert={alert} variant="alerts-screen" key={alert.alertId} />
                      })}
                    </Grid>
                  )
                })}
              </Grid>
              <Pagination
                count={Math.max(1, Math.ceil(currentAlerts.length / PAGE_SIZE))}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                sx={classes.pagination}
              />
            </>
          )}
        </>
      )}
    </DashboardScreen>
  )
}

export default Alerts
